<template>
    <div class="submit-buttons">
        <div
            v-if="! isPublished && ! isScheduled && episodeModal.hasAudioFile()"
            class="schedule-button-container"
        >
            <form-button
                :is-loading="isSaving && clickedButton === 'schedule'"
                icon="calendar-alt"
                text="Schedule"
                :disabled="isSaving || isSaved"
                @click="showScheduleDatePicker"
            />

            <date-time-picker
                v-if="isScheduleDatePickerVisible"
                v-on-clickaway="hideScheduleDatePicker"
                :min-date="new Date()"
                :value="episodeModal.getEpisodeData('publishedAt')"
                :disabled="isSaving || isSaved"
                @cancel="hideScheduleDatePicker"
                @change="scheduleEpisodePublication"
            />
        </div>

        <form-button
            :is-loading="isSaving && saveButtonClicked"
            :is-completed="isSaved && saveButtonClicked"
            :class="{ 'is-purple': !saveButtonClicked || !isSaving && !isSaved }"
            :disabled="isSaving || isSaved"
            :text="saveDraftText"
            @click="saveEpisode"
        />

        <form-button
            v-if="! isPublished && episodeModal.hasAudioFile()"
            :is-loading="isSaving && clickedButton === 'publish'"
            class="is-green"
            :disabled="isSaving || isSaved"
            :text="isScheduled ? 'Publish Now' : 'Publish Episode'"
            @click="publishEpisode"
        />
    </div>
</template>
<script>
import { isString } from 'lodash';
import moment from 'moment-timezone';
import { mixin as clickaway } from 'vue-clickaway';
import { mapMutations, mapState } from 'vuex';
import episodeModal from '@/utils/episodeModal';
import DateTimePicker from '@/components/Controls/DateTimePicker';
import FormButton from '@/components/Form/FormButton';

export default {
    components: {
        DateTimePicker,
        FormButton,
    },

    mixins: [
        clickaway,
    ],

    props: {
        publishedAt: {
            default: null,
            type: String,
        },
    },

    data: () => ({
        episodeModal,
        clickedButton: null,
        isScheduleDatePickerVisible: false,
        initialPublicationDate: null,
    }),

    computed: {
        ...mapState({
            addEpisodeModal: state => ({ ...state.UIState.addEpisodeModal }),
        }),

        /**
         * Indicates whether modal is being saved.
         *
         * @returns {boolean} Check result.
         */
        isSaving() {
            return episodeModal.isSaving();
        },

        /**
         * Indicates whether modal is saved.
         *
         * @returns {boolean} Check result.
         */
        isSaved() {
            return episodeModal.isSaved();
        },

        /**
         * Indicates whether episode is saved as draft or not.
         *
         * @returns {boolean} Check result.
         */
        isDrafted() {
            return null === this.initialPublicationDate;
        },

        /**
         * Indicates whether episode is published or not.
         *
         * @returns {boolean} Check result.
         */
        isPublished() {
            return isString(this.initialPublicationDate) &&
                moment.utc(this.initialPublicationDate).isSameOrBefore(moment.utc());
        },

        /**
         * Indicates whether episode publication is scheduled or not.
         *
         * @returns {boolean} Check result.
         */
        isScheduled() {
            return isString(this.initialPublicationDate) &&
                moment.utc(this.initialPublicationDate).isAfter(moment.utc());
        },

        /**
         * Indicates whether "Save" button was clicked or not.
         *
         * @returns {boolean} Check result.
         */
        saveButtonClicked() {
            return 'save' === this.clickedButton || ! this.clickedButton;
        },

        /**
         * Computes text for "Save" button, depending on save state.
         *
         * @returns {string} "Save" button text.
         */
        saveDraftText() {
            if (this.saveButtonClicked) {
                if (this.isSaving) {
                    return 'Saving';
                }

                if (this.isSaved) {
                    return 'Saved';
                }
            }

            return this.isPublished ? 'Save Changes' : 'Save';
        },
    },

    /**
     * Action after components is mounted.
     */
    mounted() {
        this.initialPublicationDate = this.publishedAt;
    },

    methods: {
        ...mapMutations(['changeUIState']),

        /**
         * Shows schedule date picker.
         */
        showScheduleDatePicker() {
            this.isScheduleDatePickerVisible = true;
        },

        /**
         * Hides schedule date picker.
         */
        hideScheduleDatePicker() {
            this.isScheduleDatePickerVisible = false;
        },

        /**
         * Schedules episode publication and saves it.
         *
         * @param {string} value Selected publication date.
         */
        scheduleEpisodePublication(value) {
            if (null === value) {
                return;
            }

            // If somehow selected datetime is before "now", we don't want to
            // leave "scheduled" option selected. In that case emit "now" value.
            if (moment.utc(value).isSameOrBefore(moment.utc())) {
                value = moment.utc().format('YYYY-MM-DD HH:mm:ss');
            }

            episodeModal.updateEpisode({ publishedAt: value });

            this.clickedButton = 'schedule';
            this.$emit('submit');
        },

        /**
         * Publishes episode.
         */
        publishEpisode() {
            episodeModal.updateEpisode({ publishedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss') });

            this.clickedButton = 'publish';
            this.$emit('submit');
        },

        /**
         * Saves episode (possibly without publication date).
         */
        saveEpisode() {
            this.clickedButton = 'save';
            this.$emit('submit');

            if (this.publishedAt !== this.initialPublicationDate) {
                this.initialPublicationDate = this.publishedAt;
            }
        },
    },
};
</script>
